import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { object } from 'app/utils';
import { DateBox, Field, Select, TextArea, TextBox } from 'app/components/form';
import { Form } from 'app/components/layout';
import { profiles } from 'app/services';
import { IProfile } from 'app/models';

export interface IProfileView {
  profileId: any;
  payload?: Partial<IProfile>;
}

export const ProfileView = memo(({ profileId, payload }: IProfileView) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({} as IProfile);
  const [changes, setChanges] = useState([] as string[]);

  const weight = (values.type === 2 ? 'scale' : 'experience');

  useEffect(() => {
    if (!profileId) return;

    profiles.get(profileId).then(({ success, data }) => {
      if (!success) return;

      setValues(data);
      if (!payload) return;

      const buffer: string[] = [];
      (Object.keys(payload) as Array<keyof IProfile>).forEach(i => {
        if (data[i] !== payload[i]) buffer.push(i);
      });

      setChanges(buffer);
    });
  }, [profileId, payload]);

  object.extend(values, payload);

  return (
    <Form responsive={true}>
      <Field text={t('profiles.fields.type')} align="end" asterisk>
        <Select options={[
          { label: t('app.profile.employee'), value: 1 },
          { label: t('app.profile.employer'), value: 2 },
        ]} value={values.type} disabled />
      </Field>
      <Field text={t('profiles.fields.firstName')} align="end" asterisk className="pt-4">
        <TextBox value={values.firstName} disabled className={{ 'border-green-500': changes.includes('firstName') }} />
      </Field>
      <Field text={t('profiles.fields.lastName')} align="end" asterisk className="pt-4">
        <TextBox value={values.lastName} disabled className={{ 'border-green-500': changes.includes('lastName') }} />
      </Field>
      { values.type === 2 && <Field text={t('profiles.fields.title')} align="end" asterisk className="pt-4">
        <TextBox value={values.title} disabled className={{ 'border-green-500': changes.includes('title') }} />
      </Field> }
      <Field text={t(`profiles.fields.${values.type === 2 ? 'foundationDate' : 'birthDate'}`)} align="end" asterisk className="pt-4">
        <DateBox value={values.birthDate} disabled className={{ 'border-green-500': changes.includes(values.type === 2 ? 'foundationDate' : 'birthDate') }} />
      </Field>
      { values.type === 1 && <Field text={t('profiles.fields.nationality')} align="end" asterisk className="pt-4">
        <Select source={{ type: 'lookup', key: 'countries' }} value={values.nationalityId} disabled className={{ 'border-green-500': changes.includes('nationality') }} />
      </Field> }
      <Field text={t('profiles.fields.country')} align="end" asterisk className="pt-4">
        <Select source={{ type: 'lookup', key: 'countries' }} value={values.countryId} disabled className={{ 'border-green-500': changes.includes('country') }} />
      </Field>
      <Field text={t('profiles.fields.profession')} align="end" asterisk className="pt-4">
        <Select source={{ type: 'lookup', key: 'professions', filter: (o) => (o.type === values.type) }} value={values.professionId} disabled className={{ 'border-green-500': changes.includes('profession') }} />
      </Field>
      <Field text={t(`profiles.fields.${weight}`)} align="end" asterisk className="pt-4">
        <Select options={[
          { label: t(`app.${weight}.beginner`), value: 1 },
          { label: t(`app.${weight}.intermediate`), value: 2 },
          { label: t(`app.${weight}.advanced`), value: 3 },
          { label: t(`app.${weight}.expert`), value: 4 },
          { label: t(`app.${weight}.master`), value: 5 },
        ]} value={values.experience} disabled className={{ 'border-green-500': changes.includes(weight) }} />
      </Field>
      <Field text={t('profiles.fields.description')} align="end" asterisk className="pt-4">
        <TextArea value={values.description} style={{ height: '6rem' }} disabled className={{ 'border-green-500': changes.includes('description') }} />
      </Field>
      <Field text={t('profiles.fields.status')} align="end" asterisk className="pt-4">
        <Select options={[
          { label: t('app.status.disabled'), value: 0 },
          { label: t('app.status.enabled'), value: 1 },
          { label: t('app.status.approved'), value: 2 },
        ]} value={values.status} disabled className={{ 'border-green-500': changes.includes('status') }} />
      </Field>
    </Form>
  );
});
